<template>
  <div data-component-group="filter-forms" data-component="shop">
    <FiltersForm
      :has-filters="hasFilters"
      :is-skeleton-loading="isSkeletonLoading"
      @filter="onFilter"
      @clear="onClearFilters"
      v-model="selectedCountries"
    >
      <v-text-field
        clearable
        v-model="search"
        type="search"
        label="Search"
        prepend-inner-icon="search"
      />

      <MerchantDropdown
        class="autocomplete__one-line"
        v-model="selectedMerchants"
        prepend-inner-icon="store"
        :label="$t('merchants')"
        :countries="selectedCountries"
        :hide-details="true"
        multiple
      />

      <v-autocomplete
        clearable
        v-model="category"
        :items="categories"
        prepend-inner-icon="category"
        :label="$t('category')"
        :loading="isLoadingCategory"
        :return-object="true"
        @update:search="categorySearch"
        :item-title="getCategoryNameNCountry"
      />

      <base-date-picker
        :date="dates"
        :placeholder="$t('dates_placeholder')"
        :config="dateConfig"
        :on-change="onChangeDate"
      />

      <v-autocomplete
        clearable
        v-model="status"
        :items="statuses"
        prepend-inner-icon="download_done"
        item-title="name"
        item-value="id"
        label="Status"
        @focus="onStatusFocus"
      />
    </FiltersForm>
  </div>
</template>

<script>
import FiltersForm from './FiltersBase';
import filterUrlSync from '@/mixins/filterUrlSync';
import { isNum, isNonEmptyArr, isNonEmptyStr, isStr, debounce, isObj } from '@/utils';
import { v1 as uuidv1 } from 'uuid';
import MerchantDropdown from '@/components/dropdowns/Merchant.vue';

const categorySearchDebouncer = debounce({
  id: uuidv1(),
  timeout: 500
});

export default {
  mixins: [filterUrlSync],
  components: {
    MerchantDropdown,
    FiltersForm,
  },
  props: {
    isSkeletonLoading: {
      type: Boolean,
      default: false,
    }
  },
  emits: ['filter', 'clearFilters'],
  data() {
    return {
      urlConfig: [
        { field: 'search', urlKey: 'search', type: 'string' },
        { field: 'selectedCountries', urlKey: 'countries', type: 'string[]' },
        { field: 'dates', urlKey: 'dates', type: 'string' },
        // The status select stores the raw id (item-value="id").
        { field: 'status', urlKey: 'status', type: 'number' },
        {
          // Both looked up by id: the value the URL names can sit outside the
          // page the search fetch returns, and it has to end up in the options.
          field: 'category', urlKey: 'category', type: 'number', prop: 'id',
          fetchByIds: 'getCategoriesByIds',
        },
        {
          field: 'selectedMerchants', urlKey: 'merchants', type: 'number[]', prop: 'id',
          fetchByIds: 'getMerchantsByIds',
        },
      ],
      category: null,
      dates: null,
      dateConfig: {
        mode: 'range',
      },
      search: null,
      status: null,
      selectedCountries: [],
      selectedMerchants: [],
      isLoadingCategory: false,
    };
  },
  computed: {
    hasFilters() {
      const { search, status, selectedCountries, selectedMerchants, dates, category } = this;

      return isNonEmptyStr(search) ||
        isNum(status) ||
        isNum(category?.id) ||
        isNonEmptyArr(selectedCountries) ||
        isNonEmptyArr(selectedMerchants) ||
        !!dates;
    },
    statuses() {
      return this.$store.getters['sponsorships/statuses'];
    },
    categories: {
      get() {
        const storeCategories = this.$store.getters['pepper/groups'] ?? [];
        if (isObj(this.category) && storeCategories.findIndex(c => c.id === this.category.id) === -1) {
          return [...storeCategories, this.category];
        }
        return storeCategories;
      },
      set(newCategories) {
        return newCategories;
      },
    },
  },
  methods: {
    getMerchantsByIds(ids) {
      return this.$store.dispatch('pepper/getMerchantsByIds', { ids, countries: this.selectedCountries });
    },
    getCategoriesByIds(ids) {
      this.isLoadingCategory = true;
      return this.$store
        .dispatch('pepper/getGroupsByIds', { ids })
        .finally(() => {
          this.isLoadingCategory = false;
        });
    },
    getCategoryNameNCountry(category) {
      // A category restored from a deep link is a bare { id } stub until
      // fetchByIds resolves it; render nothing rather than "undefined (undefined)".
      return category?.name ? `${category.name} (${category?.country?.code})` : '';
    },
    categorySearch(nextSearch) {
      if (isStr(nextSearch)) {
        if (this.isLoadingCategory) {
          this.isLoadingCategory = false;
          return false;
        }

        if (isNonEmptyStr(nextSearch)) {
          categorySearchDebouncer(() => this.getCategory(nextSearch));
        }
      }
    },
    getCategory(search) {
      const { selectedCountries } = this;
      const query = {
        countries: selectedCountries,
        search,
      };

      this.isLoadingCategory = true;

      return this.$store
        .dispatch('pepper/getGroups', query)
        .then(({ groups }) => {
          this.categories = groups;
          this.isLoadingCategory = false;
          return groups;
        })
        .catch((err) => {
          this.isLoadingCategory = false;
          return err;
        });
    },
    onChangeDate(dates) {
      this.dates = dates;
    },
    getStatuses() {
      return this.$store.dispatch('sponsorships/getStatuses');
    },
    onFilter() {
      const { selectedCountries, selectedMerchants, search, status, dates, category } = this;

      this.syncFiltersToUrl();

      this.$emit('filter', {
        search,
        status,
        countries: selectedCountries,
        merchants: selectedMerchants.map(m => m.id),
        categories: category ? [category.id] : null,
        // Derived from `dates` here rather than tracked separately: a URL
        // restore only ever sets `dates`, so a formattedDates tracked only by
        // onChangeDate stayed [] after a deep link and silently dropped the
        // date filter from every request that followed.
        dates: dates ? dates.split(' - ') : null,
      });
    },
    onClearFilters() {
      this.search = null;
      this.status = null;
      this.selectedCountries = [];
      this.selectedMerchants = [];
      this.category = null;
      this.dates = null;

      this.clearFiltersUrl();

      this.$emit('clearFilters');
    },
    onStatusFocus() {
      if (this.statuses.length === 0) {
        this.getStatuses();
      }
    },
  },
};
</script>
